import {
  Button,
  ButtonProps,
  Flex,
  HStack,
  Icon,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Popover } from '@ssar-app/uikit';
import {
  AiFillInfoCircle,
  AiFillDelete,
  AiOutlineReload,
  AiOutlineScissor,
  AiOutlineClose,
} from 'react-icons/ai/index.js';
import { ImRedo, ImUndo } from 'react-icons/im/index.js';

interface HeaderButtonsProps {
  disableRemove: boolean;
  hasRequiredRows: boolean;
  onRemove: () => void;
  onReset: () => void;
  cutProps: ButtonProps;
  redoProps: ButtonProps;
  undoProps: ButtonProps;
  isCutMode?: boolean;
}

export const HeaderButtons = (props: HeaderButtonsProps) => {
  const {
    disableRemove,
    hasRequiredRows,
    onRemove,
    onReset,
    cutProps,
    redoProps,
    undoProps,
    isCutMode,
  } = props;
  const commonButtonProps: ButtonProps = {
    colorScheme: 'gray',
    variant: 'outline',
    borderColor: 'gray.400',
    size: 'sm',
  };
  const disableDelete = disableRemove || hasRequiredRows;

  return (
    <Flex
      alignItems='center'
      bg='gray.100'
      px={3}
      py={2}
      borderBottom='1px solid'
      borderColor='gray.300'
      gap={4}
    >
      <Button
        colorScheme='gray'
        color='primary'
        leftIcon={<Icon as={AiOutlineReload} />}
        onClick={onReset}
        size='sm'
      >
        Reset Layout
      </Button>
      <HStack>
        <Tooltip label='Undo (Ctrl + Z)' placement='top'>
          <Button {...commonButtonProps} {...undoProps}>
            <Icon as={ImUndo} />
          </Button>
        </Tooltip>
        <Tooltip label='Redo (Ctrl + Y)' placement='top'>
          <Button {...commonButtonProps} {...redoProps}>
            <Icon as={ImRedo} />
          </Button>
        </Tooltip>
      </HStack>
      <HStack alignItems='center' ml='auto' gap={2}>
        {hasRequiredRows ? (
          <Text>
            Rows with field type
            <Text as='span' fontWeight='bold' mx={2}>
              Required/Selected
            </Text>
            cannot be deleted.
          </Text>
        ) : (
          ''
        )}
        <Tooltip label={isCutMode ? '' : 'Cut (Ctrl + X)'} placement='top'>
          <Button
            {...commonButtonProps}
            {...cutProps}
            leftIcon={
              isCutMode ? (
                <Icon as={AiOutlineClose} />
              ) : (
                <Icon as={AiOutlineScissor} />
              )
            }
          >
            {isCutMode ? 'Cancel' : 'Cut Rows'}
          </Button>
        </Tooltip>
        <Popover
          onOk={onRemove}
          message={
            <HStack>
              <Icon as={AiFillInfoCircle} color='orange.500' fontSize='lg' />
              <Text>Are you sure to delete selected rows?</Text>
            </HStack>
          }
          okText='Yes'
          cancelText='No'
          okProps={{ colorScheme: 'blue', size: 'sm' }}
          cancelProps={{ size: 'sm' }}
          placement='top-start'
          triggerButtonProps={{
            colorScheme: 'gray',
            variant: 'outline',
            borderWidth: '1px solid',
            borderColor: 'gray.300',
            leftIcon: <Icon as={AiFillDelete} />,
            isDisabled: disableDelete,
            size: 'sm',
            ...(!disableDelete ? { color: 'primary' } : {}),
          }}
        >
          Delete selected rows
        </Popover>
      </HStack>
    </Flex>
  );
};
